<template>
	<div class="chat-page-wrapper">
		<div class="chat-top-strip">
			<div class="chat-top-inner">
				<div class="chat-top-center">
					<div class="chat-top-search">
						<el-input
							placeholder="搜索联系人/群/消息"
							v-model="topSearchText"
							clearable>
						</el-input>
					</div>
				</div>
			</div>
		</div>
		<el-container class="contact-page">
			<el-aside :width="contactColumnWidth" class="aside">
				<div class="header">
					<div class="header-inner">
						<el-input
							class="search-text"
							size="small"
							placeholder="输入用户名或昵称按 Enter 搜索，支持添加新好友"
							v-model="searchText"
							@keyup.enter.native="onSearchUser"
						>
							<i class="el-icon-search el-input__icon" slot="prefix" @click="onSearchUser"></i>
						</el-input>
						<div class="header-plus" @click.stop="showContactPlusMenu = !showContactPlusMenu">
							+
							<div v-if="showContactPlusMenu" class="header-plus-menu" @click.stop>
								<div class="menu-item" @click="openAddFriendDialog(); showContactPlusMenu = false">
									<i class="el-icon-user"></i>
									<span>添加好友</span>
								</div>
								<div class="menu-item" @click="openCreateGroupDialog(); showContactPlusMenu = false">
									<i class="el-icon-chat-line-round"></i>
									<span>发起群聊</span>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="contact-menu">
					<div
						class="menu-item"
						:class="{ active: activeMenu === 'new' }"
						@click="setActiveMenu('new')"
					>
						<span class="menu-icon menu-icon-new">
							<i class="el-icon-user"></i>
							<span class="menu-icon-plus">+</span>
						</span>
						<span>新的朋友</span>
					</div>
					<div
						class="menu-item"
						:class="{ active: activeMenu === 'friends' }"
						@click="setActiveMenu('friends')"
					>
						<span class="menu-icon menu-icon-friend">
							<i class="el-icon-user"></i>
						</span>
						<span>我的好友</span>
					</div>
					<div
						class="menu-item"
						:class="{ active: activeMenu === 'groups' }"
						@click="setActiveMenu('groups')"
					>
						<span class="menu-icon menu-icon-group">
							<i class="el-icon-chat-line-round"></i>
							<i class="el-icon-chat-line-round menu-icon-group-mini"></i>
						</span>
						<span>我的群聊</span>
					</div>
				</div>
			</el-aside>
			<el-container class="container">
				<!-- 中间：好友 / 群聊列表 -->
				<el-aside
					:width="contactColumnWidth"
					class="list-aside"
					:class="{ 'has-content-title': showContactContentTitle, 'is-new-friends': activeMenu === 'new' && !isGlobalSearching }"
				>
					<div v-if="showContactContentTitle" class="contact-content-title">{{ contactContentTitle }}</div>
					<div v-if="showFriendIndex" class="friend-index-rail" aria-label="好友字母索引">
						<button
							v-for="key in friendKeys"
							:key="'friend-index-' + key"
							type="button"
							class="contact-group-letter"
							:class="{ active: activeFriendIndexKey === key }"
							:title="'跳转到 ' + friendIndexLabel(key) + ' 分组'"
							@click.stop="scrollToFriendGroup(key)"
						>
							{{ friendIndexLabel(key) }}
						</button>
					</div>
					<el-scrollbar ref="contactItems" class="contact-items">
						<div class="section global-search-section" v-if="isGlobalSearching">
							<div v-if="hasGlobalSearchResults">
								<div v-if="globalFriendResults.length" class="global-search-block">
									<div class="section-title">好友</div>
									<div v-for="friend in globalFriendResults" :key="'gf-' + friend.id" class="global-search-row" @click.stop="showUserDetail(friend)">
										<friend-item :friend="friend" :active="isSelectedUser(friend)"></friend-item>
									</div>
								</div>
								<div v-if="globalGroupResults.length" class="global-search-block">
									<div class="section-title">群聊</div>
								<div v-for="group in globalGroupResults" :key="'gg-' + group.id" class="global-search-row" @click.stop="openGroupChat(group)">
									<group-item :group="group" :active="isActiveGroup(group)"></group-item>
									</div>
								</div>
								<div v-if="globalMessageResults.length" class="global-search-block">
									<div class="section-title">消息</div>
									<div v-for="item in globalMessageResults" :key="'gm-' + item.key" class="global-message-row" @click.stop="openChatFromSearch(item)">
										<head-image :name="item.title" :url="item.headImage" :size="36"></head-image>
										<div class="global-message-info">
											<div class="global-message-title">{{ item.title }}</div>
											<div class="global-message-content">{{ item.content }}</div>
										</div>
									</div>
								</div>
							</div>
							<div v-else class="list-empty-tip">暂无搜索结果</div>
						</div>
						<div class="section" v-else-if="activeMenu === 'new'">
							<div v-if="friendRequests.length">
								<div v-for="request in friendRequests" :key="'request-' + request.id" class="friend-request-summary">
									<div class="friend-request-avatar">
										<head-image :name="request.fromUserNickName" :url="request.fromUserHeadImage" :size="42"></head-image>
									</div>
									<div class="friend-request-name">{{ request.fromUserNickName }}</div>
									<div class="friend-request-time">{{ formatRequestDate(request) }}</div>
									<div class="friend-request-status" :class="'status-' + request.status">
										{{ formatRequestStatus(request.status) }}
									</div>
									<div v-if="request.status === 0" class="friend-request-buttons">
										<el-button size="mini" type="primary" @click.stop="acceptFriendRequest(request)">同意</el-button>
										<el-button size="mini" plain @click.stop="rejectFriendRequest(request)">拒绝</el-button>
									</div>
								</div>
							</div>
							<div v-else class="list-empty-tip">暂无好友申请</div>
						</div>
						<div class="section" v-if="!isGlobalSearching && activeMenu === 'friends' && searchUsers.length">
							<div class="section-title">搜索结果</div>
							<div v-for="user in searchUsers" :key="'s-' + user.id">
								<div class="search-user-item" @click.stop="showUserDetail(user)">
									<div class="search-user-main">
										<friend-item :friend="user" :active="isSelectedUser(user)"></friend-item>
									</div>
									<div class="search-user-actions">
										<el-button
											size="mini"
											type="primary"
											v-if="!isFriend(user.id)"
											@click.stop="onAddFriend(user)"
										>
											添加
										</el-button>
										<el-button
											size="mini"
											type="info"
											plain
											disabled
											v-else
										>
											已添加
										</el-button>
									</div>
								</div>
							</div>
							<div class="divider" v-if="friendValues.length || groupValues.length"></div>
						</div>
						<div class="section" v-if="!isGlobalSearching && activeMenu === 'friends' && friendValues.length">
							<div
								v-for="(friends, i) in friendValues"
								:key="'f-' + friendKeys[i]"
								:ref="'friendGroup-' + friendKeys[i]"
								class="contact-group"
							>
								<div class="letter">{{ friendKeys[i] }}</div>
								<div v-for="friend in friends" :key="friend.id">
									<friend-item :friend="friend" :active="isSelectedUser(friend)"
										@click.native.stop="showUserDetail(friend)"
										@dblclick.native.stop="openFriendChat(friend)"></friend-item>
								</div>
								<div v-if="i < friendValues.length - 1" class="divider"></div>
							</div>
						</div>
						<div class="section" v-if="!isGlobalSearching && activeMenu === 'groups' && groupValues.length">
							<div v-for="(groups, i) in groupValues" :key="'g-' + i">
								<div class="letter">{{ groupKeys[i] }}</div>
								<div v-for="group in groups" :key="group.id">
									<group-item :group="group" :active="isActiveGroup(group)"
										@click.native.stop="showGroupDetail(group)"
										@dblclick.native.stop="openGroupChat(group)"></group-item>
								</div>
								<div v-if="i < groupValues.length - 1" class="divider"></div>
							</div>
						</div>
					</el-scrollbar>
				</el-aside>
				<!-- 右侧：用户详情 -->
				<el-main class="detail-main">
					<div v-if="activeMenu === 'new' && !isGlobalSearching"></div>
					<div v-else-if="activeMenu === 'groups' && selectedGroup" class="group-detail-page">
						<div class="contact-group-detail">
								<chat-group-side
									:group="selectedGroup"
									:groupMembers="selectedGroupMembers"
									:show-send-message-button="true"
									@send-message="openGroupChat(selectedGroup)"
									@reload="loadSelectedGroupDetail(selectedGroup.id)">
							</chat-group-side>
						</div>
					</div>
					<div v-else-if="!selectedUser" class="empty-tip">
						{{ activeMenu === 'groups' ? '请选择左侧群聊查看详情，双击进入聊天' : '请选择左侧好友查看详情' }}
					</div>
					<div v-else class="user-detail-page">
						<div class="detail-header">
							<div class="user-name">{{ selectedUser.nickName || selectedUser.userName }}</div>
						</div>
						<div class="detail-body">
							<div class="avatar-section">
								<head-image :name="selectedUser.nickName" :url="selectedUser.headImage" :size="100" :online="selectedUser.online" radius="12px"></head-image>
							</div>
							<div class="basic-section">
								<div class="user-display-name">{{ displayName }}</div>
							</div>
							<div class="info-section">
								<div class="info-row">
									<div class="label">用户名(ID)</div>
									<div class="value">{{ selectedUser.userName || '-' }}</div>
								</div>
								<div class="info-row">
									<div class="label">昵称</div>
									<div class="value">{{ selectedUser.nickName || '-' }}</div>
								</div>
								<div class="info-row">
									<div class="label">备注名</div>
									<div class="value">{{ remarkMap[selectedUser.id] || 'huohu123' }}</div>
								</div>
								<div class="info-row">
									<div class="label">性别</div>
									<div class="value">{{ selectedUser.sex === 0 ? '男' : selectedUser.sex === 1 ? '女' : '未知' }}</div>
								</div>
								<div class="info-row">
									<div class="label">个性签名</div>
									<div class="value">{{ selectedUser.signature || '这个人很懒，什么都没有留下' }}</div>
								</div>
								<div class="info-row">
									<div class="label">我的简介</div>
									<div class="value">{{ selectedUserDetail.selfDescrip || selectedUser.intro || '暂无' }}</div>
								</div>
								<div class="info-row">
									<div class="label">我的联系方式</div>
									<div class="value">{{ selectedUserDetail.selfContact || selectedUser.contact || '暂无' }}</div>
								</div>
								<div class="info-row">
									<div class="label">我的照片</div>
									<div class="value photos">
										<div class="photo-container" v-if="selectedUserDetail.selfImages && selectedUserDetail.selfImages.length">
											<div class="photo-item" v-for="(photo, index) in selectedUserDetail.selfImages" :key="index">
												<img :src="photo.thumbUrl || photo.originUrl || photo" class="photo-image" @click="previewImage(photo.originUrl || photo)" />
											</div>
										</div>
										<div class="photo-placeholder" v-else>暂无照片</div>
									</div>
								</div>
								<div class="info-row">
									<div class="label">我的视频</div>
									<div class="value videos">
										<template v-if="selectedUserDetail.selfVideo">
											<video
												class="video-player"
												:src="selectedUserDetail.selfVideo"
												:poster="selectedUserDetail.selfVideoThumb || ''"
												controls
											></video>
										</template>
										<div v-else>暂无视频</div>
									</div>
								</div>
							</div>
						</div>
						<div class="detail-footer">
							<template v-if="selectedUser && isFriend(selectedUser.id)">
								<el-button type="primary" size="medium" @click="sendMessage">发送消息</el-button>
								<el-button type="danger" size="medium" @click="deleteFriend">删除好友</el-button>
							</template>
							<template v-else>
								<el-button type="primary" size="medium" @click="onAddFriend(selectedUser)">添加好友</el-button>
							</template>
						</div>
					</div>
				</el-main>
			</el-container>
		</el-container>
	</div>
</template>

<script>
import FriendItem from "../components/friend/FriendItem.vue";
import GroupItem from "../components/group/GroupItem.vue";
import HeadImage from "../components/common/HeadImage.vue";
import ChatGroupSide from "../components/chat/ChatGroupSide.vue";
import friendStore from "../store/friendStore";
import groupStore from "../store/groupStore";
import chatStore from "../store/chatStore";
import userStore from "../store/userStore";
import { pinyin } from "pinyin-pro";
import { getPendingFriendRequestCount, mergeFriendRequestsBySender } from "../utils/friendRequest";

export default {
	name: "contact",
	components: {
		FriendItem,
		GroupItem,
		HeadImage,
		ChatGroupSide,
	},
	data() {
		return {
			topSearchText: "",
			searchText: "",
			friendStore: friendStore(),
			groupStore: groupStore(),
			chatStore: chatStore(),
			userStore: userStore(),
			searchUsers: [],
			friendRequests: [],
			pendingRequestCount: 0,
			selectedUser: null,
			selectedGroupId: null,
			selectedGroupMembers: [],
			// 备注映射（本地存储）
			remarkMap: {},
			// Web 端单独维护的用户详情，用于展示“我的照片”“我的视频”等
			selectedUserDetail: {
				selfDescrip: '',
				selfImages: [],
				selfVideo: '',
				selfVideoThumb: '',
				selfContact: ''
			},
			contactColumnWidth: '260px',
			// 通讯录列表顶部 + 按钮菜单显隐
			showContactPlusMenu: false,
			// 左侧三行菜单当前选中项：new / friends / groups
			activeMenu: 'friends',
			activeFriendIndexKey: '',
		};
	},
	computed: {
		displayName() {
			if (!this.selectedUser) return '';
			return this.remarkMap[this.selectedUser.id] || this.selectedUser.nickName || this.selectedUser.userName;
		},
		selectedGroup() {
			if (!this.selectedGroupId) return null;
			return this.groupStore.findGroup(this.selectedGroupId) || null;
		},
		globalSearchKeyword() {
			return (this.topSearchText || '').trim();
		},
		isGlobalSearching() {
			return !!this.globalSearchKeyword;
		},
		globalFriendResults() {
			const keyword = this.globalSearchKeyword;
			if (!keyword) return [];
			return this.friendStore.friends.filter((friend) => {
				if (friend.deleted) return false;
				return this.matchesKeyword([
					this.getFriendDisplayName(friend),
					friend.nickName,
					friend.userName,
					friend.remarkNickName
				], keyword);
			});
		},
		globalGroupResults() {
			const keyword = this.globalSearchKeyword;
			if (!keyword) return [];
			return this.groupStore.groups.filter((group) => {
				if (group.quit) return false;
				return this.matchesKeyword([
					this.getGroupDisplayName(group),
					group.showGroupName,
					group.name
				], keyword);
			});
		},
		globalMessageResults() {
			const keyword = this.globalSearchKeyword;
			if (!keyword) return [];
			const results = [];
			const chats = this.chatStore.findChats ? this.chatStore.findChats() : [];
			(chats || []).forEach((chat) => {
				if (!chat || chat.delete || !chat.messages) return;
				const title = this.getChatTitle(chat);
				const headImage = this.getChatHeadImage(chat);
				chat.messages.forEach((message, index) => {
					const content = this.formatSearchMessageContent(message);
					if (!content || !this.matchesKeyword([content], keyword)) return;
					results.push({
						key: `${chat.type}-${chat.targetId}-${message.id || message.tmpId || index}`,
						chat,
						title,
						headImage,
						content
					});
				});
			});
			return results.slice(0, 30);
		},
		hasGlobalSearchResults() {
			return this.globalFriendResults.length || this.globalGroupResults.length || this.globalMessageResults.length;
		},
		friendMap() {
			let map = new Map();
			this.friendStore.friends.forEach((f) => {
				if (f.deleted) return;
				// 搜索同时匹配昵称和用户名
				let nick = f.nickName || "";
				let userName = f.userName || "";
				let searchable = nick + " " + userName;
				if (this.searchText && !searchable.includes(this.searchText)) return;
				// 用昵称优先，其次用户名来生成分组首字母
				let displayName = nick || userName;
				let letter = this.firstLetter(displayName).toUpperCase();
				if (!this.isEnglish(letter)) letter = "#";
				if (f.online) letter = "在线";
				if (map.has(letter)) map.get(letter).push(f);
				else map.set(letter, [f]);
			});
			let arr = Array.from(map);
			arr.sort((a, b) => {
				if (a[0] === "#" || b[0] === "#") {
					return b[0].localeCompare(a[0]);
				}
				return a[0].localeCompare(b[0]);
			});
			map = new Map(arr.map((i) => [i[0], i[1]]));
			return map;
		},
		friendKeys() {
			return Array.from(this.friendMap.keys());
		},
		friendValues() {
			return Array.from(this.friendMap.values());
		},
		showFriendIndex() {
			return !this.isGlobalSearching
				&& this.activeMenu === 'friends'
				&& !this.searchUsers.length
				&& this.friendKeys.length > 0;
		},
		groupMap() {
			let map = new Map();
			this.groupStore.groups.forEach((g) => {
				if (g.quit) return;
				let name = g.showGroupName || g.name || "";
				if (this.searchText && !name.includes(this.searchText)) return;
				let letter = this.firstLetter(name).toUpperCase();
				if (!this.isEnglish(letter)) letter = "#";
				if (map.has(letter)) map.get(letter).push(g);
				else map.set(letter, [g]);
			});
			let arr = Array.from(map);
			arr.sort((a, b) => {
				if (a[0] === "#" || b[0] === "#") {
					return b[0].localeCompare(a[0]);
				}
				return a[0].localeCompare(b[0]);
			});
			map = new Map(arr.map((i) => [i[0], i[1]]));
			return map;
		},
		groupKeys() {
			return Array.from(this.groupMap.keys());
		},
		groupValues() {
			return Array.from(this.groupMap.values());
		},
		showContactContentTitle() {
			if (this.isGlobalSearching) {
				return true;
			}
			if (this.activeMenu === 'new') {
				return true;
			}
			if (this.activeMenu === 'friends') {
				return !this.searchUsers.length;
			}
			if (this.activeMenu === 'groups') {
				return true;
			}
			return false;
		},
		contactContentTitle() {
			if (this.isGlobalSearching) return '搜索结果';
			if (this.activeMenu === 'new') return '好友申请';
			if (this.activeMenu === 'friends') return '我的好友';
			if (this.activeMenu === 'groups') return '我的群聊';
			return '';
		},
	},
	watch: {
		friendKeys: {
			immediate: true,
			handler(keys) {
				if (!keys.includes(this.activeFriendIndexKey)) {
					this.activeFriendIndexKey = keys[0] || '';
				}
				this.$nextTick(() => this.updateActiveFriendIndex());
			}
		}
	},
	methods: {
		friendIndexLabel(key) {
			return key === '在线' ? '*' : key;
		},
		getContactScrollWrap() {
			const scrollbar = this.$refs.contactItems;
			return scrollbar && scrollbar.$el
				? scrollbar.$el.querySelector('.el-scrollbar__wrap')
				: null;
		},
		getFriendGroupElement(key) {
			const groupRef = this.$refs['friendGroup-' + key];
			return Array.isArray(groupRef) ? groupRef[0] : groupRef;
		},
		scrollToFriendGroup(key) {
			this.activeFriendIndexKey = key;
			this.$nextTick(() => {
				const wrap = this.getContactScrollWrap();
				const target = this.getFriendGroupElement(key);
				if (!wrap || !target) return;
				const wrapRect = wrap.getBoundingClientRect();
				const targetRect = target.getBoundingClientRect();
				const targetTop = Math.max(0, wrap.scrollTop + targetRect.top - wrapRect.top);
				if (typeof wrap.scrollTo === 'function') {
					wrap.scrollTo({
						top: targetTop,
						behavior: 'smooth'
					});
				} else {
					wrap.scrollTop = targetTop;
				}
			});
		},
		bindContactIndexScroll() {
			const wrap = this.getContactScrollWrap();
			if (!wrap || this._contactIndexScrollWrap === wrap) return;
			this.unbindContactIndexScroll();
			this._contactIndexScrollWrap = wrap;
			wrap.addEventListener('scroll', this.handleContactIndexScroll, { passive: true });
		},
		unbindContactIndexScroll() {
			if (this._contactIndexScrollWrap) {
				this._contactIndexScrollWrap.removeEventListener('scroll', this.handleContactIndexScroll);
				this._contactIndexScrollWrap = null;
			}
			if (this._contactIndexScrollFrame) {
				window.cancelAnimationFrame(this._contactIndexScrollFrame);
				this._contactIndexScrollFrame = null;
			}
		},
		handleContactIndexScroll() {
			if (this._contactIndexScrollFrame) return;
			this._contactIndexScrollFrame = window.requestAnimationFrame(() => {
				this._contactIndexScrollFrame = null;
				this.updateActiveFriendIndex();
			});
		},
		updateActiveFriendIndex() {
			if (!this.showFriendIndex) return;
			const wrap = this.getContactScrollWrap();
			if (!wrap) return;
			if (wrap.scrollTop + wrap.clientHeight >= wrap.scrollHeight - 2) {
				this.activeFriendIndexKey = this.friendKeys[this.friendKeys.length - 1] || '';
				return;
			}
			const threshold = wrap.getBoundingClientRect().top + 2;
			let activeKey = this.friendKeys[0] || '';
			for (const key of this.friendKeys) {
				const group = this.getFriendGroupElement(key);
				if (!group || group.getBoundingClientRect().top > threshold) break;
				activeKey = key;
			}
			this.activeFriendIndexKey = activeKey;
		},
		loadRemarks() {
			try {
				const remarks = localStorage.getItem('friend_remarks');
				if (remarks) {
					this.remarkMap = JSON.parse(remarks);
				}
			} catch (error) {
				console.error('加载备注失败:', error);
			}
		},
		showUserDetail(user) {
			// 先用列表里的用户对象做初始展示
			this.selectedGroupId = null;
			this.selectedGroupMembers = [];
			this.selectedUser = user;
			if (user && user.id) {
				const selectedUserId = user.id;
				// 1）补充加载完整用户信息（包含 signature 等字段）
				this.$http({
					url: `/user/find/${user.id}`,
					method: 'GET',
				}).then(fullUser => {
					if (fullUser && this.selectedUser && this.selectedUser.id === selectedUserId) {
						// 合并：保留列表里的在线状态等，同时补全签名等字段
						this.selectedUser = Object.assign({}, this.selectedUser, fullUser);
					}
				}).catch(err => {
					console.error('加载用户基础信息失败:', err);
				});

				// 2）加载用户详情（t_user_detail），用于简介/照片/视频等
				this.loadUserDetail(user.id);
			}
		},
		isSelectedUser(user) {
			return this.selectedUser && this.selectedUser.id === user.id;
		},
		normalizeSearchText(text) {
			return String(text || '').trim().toLowerCase();
		},
		matchesKeyword(values, keyword) {
			const normalizedKeyword = this.normalizeSearchText(keyword);
			if (!normalizedKeyword) return true;
			return values.some((value) => this.normalizeSearchText(value).includes(normalizedKeyword));
		},
		getFriendDisplayName(friend) {
			if (!friend) return '';
			return this.remarkMap[friend.id] || friend.nickName || friend.userName || '';
		},
		getGroupDisplayName(group) {
			if (!group) return '';
			return group.showGroupName || group.name || '';
		},
		getChatTitle(chat) {
			if (!chat) return '';
			if (chat.type === 'PRIVATE') {
				const friend = this.friendStore.findFriend(chat.targetId);
				return this.getFriendDisplayName(friend) || chat.showName || chat.originalName || '';
			}
			if (chat.type === 'GROUP') {
				const group = this.groupStore.findGroup(chat.targetId);
				return this.getGroupDisplayName(group) || chat.showName || '';
			}
			return chat.showName || '';
		},
		getChatHeadImage(chat) {
			if (!chat) return '';
			if (chat.type === 'PRIVATE') {
				const friend = this.friendStore.findFriend(chat.targetId);
				return (friend && (friend.headImageThumb || friend.headImage)) || chat.headImage || '';
			}
			if (chat.type === 'GROUP') {
				const group = this.groupStore.findGroup(chat.targetId);
				return (group && (group.headImageThumb || group.headImage)) || chat.headImage || '';
			}
			return chat.headImage || '';
		},
		formatSearchMessageContent(message) {
			if (!message || !message.content) return '';
			return String(message.content).replace(/\s+/g, ' ').trim();
		},
		sendMessage() {
			if (!this.selectedUser) return;
			let chat = {
				type: 'PRIVATE',
				targetId: this.selectedUser.id,
				showName: this.selectedUser.nickName,
				headImage: this.selectedUser.headImageThumb,
			};
			this.chatStore.openChat(chat);
			this.chatStore.setActiveChat(0);
			this.$router.push("/home/chat");
		},
		// 从后端加载用户详情（与 App 端 user-info.vue 逻辑保持一致）
		loadUserDetail(userId) {
			this.$http({
				url: `/user/detail/${userId}`,
				method: "GET",
			}).then((data) => {
				if (!data) return;
				// 处理图片数据：拼出 { originUrl, thumbUrl }
				let processedImages = [];
				if (data.selfImages && data.selfImages.length > 0) {
					processedImages = data.selfImages.map((imgUrl, index) => {
						const thumbUrl = (data.selfImageThumbs && data.selfImageThumbs[index]) || imgUrl;
						return {
							originUrl: imgUrl,
							thumbUrl: thumbUrl,
						};
					});
				}
				this.selectedUserDetail = {
					selfDescrip: data.selfDescrip || '',
					selfImages: processedImages,
					selfVideo: data.selfVideo || '',
					selfVideoThumb: data.selfVideoThumb || '',
					selfContact: data.selfContact || '',
				};
			}).catch((err) => {
				console.error('获取用户详情失败:', err);
			});
		},
		// 简单图片预览：新窗口打开原图
		previewImage(url) {
			if (!url) return;
			window.open(url, '_blank');
		},
		deleteFriend() {
			if (!this.selectedUser) return;
			const friend = this.selectedUser;
			this.$confirm(`删除联系人“${this.displayName}”后，是否同时清空聊天记录？`, '删除联系人', {
				confirmButtonText: '删除并清空',
				cancelButtonText: '仅删除联系人',
				distinguishCancelAndClose: true,
				type: 'warning'
			}).then(() => {
				this.performDeleteFriend(friend, true);
			}).catch((action) => {
				if (action === 'cancel') {
					this.performDeleteFriend(friend, false);
				}
			});
		},
		performDeleteFriend(friend, clearChatHistory) {
			this.$http({
				url: `/friend/delete/${friend.id}`,
				method: 'DELETE'
			}).then(() => {
				this.$message.success('删除联系人成功');
				this.friendStore.removeFriend(friend.id);
				if (clearChatHistory) {
					this.chatStore.removePrivateChat(friend.id);
				}
				if (this.selectedUser && this.selectedUser.id === friend.id) {
					this.selectedUser = null;
				}
			}).catch((err) => {
				this.$message.error(err.message || '删除联系人失败');
			});
		},
		loadPendingRequests() {
			this.$http({
				url: "/friend-request/received",
				method: "GET",
			}).then((data) => {
				this.pendingRequestCount = getPendingFriendRequestCount(data);
			}).catch(() => {
				this.pendingRequestCount = 0;
			});
		},
		loadFriendRequests() {
			return this.$http({
				url: "/friend-request/received",
				method: "GET",
			}).then((data) => {
				this.onFriendRequestsUpdated(data);
			}).catch(() => {
				this.friendRequests = [];
			});
		},
		onFriendRequestsUpdated(requests) {
			this.friendRequests = mergeFriendRequestsBySender(requests);
			this.pendingRequestCount = getPendingFriendRequestCount(this.friendRequests);
		},
		onFriendRequestChanged() {
			this.loadPendingRequests();
			if (this.activeMenu === 'new') {
				this.loadFriendRequests();
			}
		},
		markFriendRequestHandled(request, status) {
			this.friendRequests = (this.friendRequests || []).map((item) => {
				if (item.id === request.id) {
					return { ...item, status };
				}
				return item;
			});
			this.pendingRequestCount = getPendingFriendRequestCount(this.friendRequests);
			sessionStorage.setItem('friendRequestCount', this.pendingRequestCount);
			this.$eventBus.$emit('updateFriendRequestCount', this.pendingRequestCount);
		},
		acceptFriendRequest(request) {
			this.$http({
				url: "/friend-request/accept/" + request.id,
				method: "POST",
			}).then(() => {
				this.$message.success("已同意好友申请");
				this.markFriendRequestHandled(request, 1);
				this.friendStore.loadFriend();
			}).catch((error) => {
				this.$message.error(error.message || "操作失败");
			});
		},
		rejectFriendRequest(request) {
			this.$http({
				url: "/friend-request/reject/" + request.id,
				method: "POST",
			}).then(() => {
				this.$message.success("已拒绝好友申请");
				this.markFriendRequestHandled(request, 2);
			}).catch((error) => {
				this.$message.error(error.message || "操作失败");
			});
		},
		formatRequestStatus(status) {
			if (status === 1) return '已同意';
			if (status === 2) return '已拒绝';
			return '待处理';
		},
		formatRequestDate(request) {
			const rawTime = request && (request.createdTime || request.createTime || request.createdAt);
			if (!rawTime) return '';
			const date = new Date(rawTime);
			if (Number.isNaN(date.getTime())) return '';
			return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
		},
		openCreateGroupDialog() {
			this.$router.push({
				path: "/home/group",
				query: { create: '1', t: Date.now() }
			});
		},
		goToFriendRequest() {
			this.$router.push("/home/friend-request");
		},
		openAddFriendDialog() {
			this.$router.push({
				path: "/home/friend",
				query: { addFriend: '1', t: Date.now() }
			});
		},
		setActiveMenu(type) {
			if (type === 'new') {
				this.activeMenu = 'new';
				this.searchUsers = [];
				this.selectedUser = null;
				this.selectedGroupId = null;
				this.selectedGroupMembers = [];
				this.loadFriendRequests();
				return;
			}
			this.activeMenu = type;
			this.selectedGroupId = null;
			this.selectedGroupMembers = [];
			if (type === 'groups') {
				this.selectedUser = null;
				this.selectedUserDetail = {
					selfDescrip: '',
					selfImages: [],
					selfVideo: '',
					selfVideoThumb: '',
					selfContact: ''
				};
			}
		},
		// 格式化未读数量，当大于99时显示99+
		formatBadgeCount(count) {
			if (count > 99) {
				return "99+";
			}
			return count;
		},
		onSearchUser() {
			const keyword = (this.searchText || "").trim();
			if (!keyword) {
				this.searchUsers = [];
				return;
			}
			this.$http({
				url: "/user/findByName",
				method: "get",
				params: { name: keyword },
			}).then((data) => {
				const list = data || [];
				const selfId = this.userStore && this.userStore.userInfo && this.userStore.userInfo.id;
				// 过滤掉自己
				this.searchUsers = selfId ? list.filter(u => u.id !== selfId) : list;
				this.selectedUser = null;
				// 同步在线状态到好友列表，使“我的好友”中的绿点与搜索结果一致
				(list || []).forEach(u => {
					const f = this.friendStore.findFriend(u.id);
					if (f) {
						f.onlineWeb = typeof u.onlineWeb !== 'undefined' ? u.onlineWeb : f.onlineWeb;
						f.onlineApp = typeof u.onlineApp !== 'undefined' ? u.onlineApp : f.onlineApp;
						f.online = typeof u.online !== 'undefined'
							? !!u.online
							: !!(f.onlineWeb || f.onlineApp);
					}
				});
			}).catch(() => {
				this.searchUsers = [];
			});
		},
		onAddFriend(user) {
			// 直接复用原 AddFriend 里的逻辑
			this.$http({
				url: "/friend-request/check-sent/" + user.id,
				method: "GET",
			}).then((hasSent) => {
				if (hasSent) {
					this.$message.warning("您已经向对方发送过好友申请，请等待对方处理");
					return;
				}
				this.$http({
					url: "/friend-request/check-received/" + user.id,
					method: "GET",
				}).then((hasReceived) => {
					if (hasReceived) {
						this.$message.warning("对方已经向您发送过好友申请，请先处理");
						return;
					}
					this.$http({
						url: "/friend-request/send",
						method: "POST",
						params: { toUserId: user.id },
					}).then(() => {
						this.$message.success("好友申请已发送，请等待对方处理");
					}).catch((error) => {
						this.$message.error(error.message || "发送申请失败");
					});
				}).catch(() => {
					this.$message.error("检查申请状态失败");
				});
			}).catch(() => {
				this.$message.error("检查申请状态失败");
			});
		},
		isFriend(userId) {
			return this.friendStore.isFriend(userId);
		},
		openUserInfo(user) {
			// 复用已有的用户详情逻辑（Home.vue 里的 user-info 组件）
			this.$eventBus.$emit('openUserInfo', user, null);
		},
		openFriendChat(friend) {
			let chat = {
				type: "PRIVATE",
				targetId: friend.id,
				showName: friend.nickName,
				headImage: friend.headImageThumb,
			};
			this.chatStore.openChat(chat);
			this.chatStore.setActiveChat(0);
			this.$router.push("/home/chat");
		},
		showGroupDetail(group) {
			if (!group || !group.id) return;
			this.selectedUser = null;
			this.selectedGroupId = group.id;
			this.selectedGroupMembers = [];
			this.loadSelectedGroupDetail(group.id);
		},
		loadSelectedGroupDetail(groupId) {
			if (!groupId) return;
			this.$http({
				url: `/group/find/${groupId}`,
				method: 'GET'
			}).then((group) => {
				if (group && this.selectedGroupId === groupId) {
					this.groupStore.updateGroup(group);
				}
			}).catch((error) => {
				console.error('加载群聊资料失败:', error);
			});
			this.$http({
				url: `/group/members/${groupId}`,
				method: 'GET'
			}).then((members) => {
				if (this.selectedGroupId === groupId) {
					this.selectedGroupMembers = members || [];
				}
			}).catch((error) => {
				console.error('加载群成员失败:', error);
				if (this.selectedGroupId === groupId) {
					this.selectedGroupMembers = [];
				}
			});
		},
		openGroupChat(group) {
			this.selectedGroupId = group.id;
			let chat = {
				type: "GROUP",
				targetId: group.id,
				showName: group.showGroupName,
				headImage: group.headImageThumb,
			};
			this.chatStore.openChat(chat);
			this.chatStore.setActiveChat(0);
			this.$router.push("/home/chat");
		},
		openChatFromSearch(item) {
			if (!item || !item.chat) return;
			const chat = item.chat;
			if (chat.type === 'GROUP') {
				const group = this.groupStore.findGroup(chat.targetId) || {
					id: chat.targetId,
					showGroupName: item.title,
					headImageThumb: item.headImage,
				};
				this.openGroupChat(group);
				return;
			}
			const friend = this.friendStore.findFriend(chat.targetId) || {
				id: chat.targetId,
				nickName: item.title,
				headImageThumb: item.headImage,
			};
			this.openFriendChat(friend);
		},
		isActiveFriend(friend) {
			const active = this.chatStore.findChatByFriend(friend.id);
			return !!active;
		},
		isActiveGroup(group) {
			return !!group && this.selectedGroupId === group.id;
		},
		firstLetter(text) {
			if (!text) return "#";
			let py = pinyin(text, { toneType: "none", type: "normal" });
			return py[0];
		},
		isEnglish(ch) {
			return /^[A-Za-z]+$/.test(ch);
		},
		handleGlobalClick() {
			if (this.showContactPlusMenu) {
				this.showContactPlusMenu = false;
			}
		},
	},
	mounted() {
		this.loadPendingRequests();
		this.loadRemarks();
		this.$nextTick(() => {
			this.bindContactIndexScroll();
			this.updateActiveFriendIndex();
		});
		// 好友申请数量同步
		this.$eventBus && this.$eventBus.$on('updateFriendRequestCount', (count) => {
			this.pendingRequestCount = count;
		});
		this.$eventBus && this.$eventBus.$on('friendRequestChanged', this.onFriendRequestChanged);
		// 从其他页面（例如聊天名片）打开某个用户详情
		this.$eventBus && this.$eventBus.$on('openContactUser', (user) => {
			this.showUserDetail(user);
		});
		// 监听全局点击和右键，点击其它区域时关闭左侧 + 菜单
		document.addEventListener('click', this.handleGlobalClick);
		document.addEventListener('contextmenu', this.handleGlobalClick);
	},
	beforeDestroy() {
		this.unbindContactIndexScroll();
		this.$eventBus && this.$eventBus.$off('updateFriendRequestCount');
		this.$eventBus && this.$eventBus.$off('friendRequestChanged', this.onFriendRequestChanged);
		this.$eventBus && this.$eventBus.$off('openContactUser');
		document.removeEventListener('click', this.handleGlobalClick);
		document.removeEventListener('contextmenu', this.handleGlobalClick);
	}
};
</script>

<style scoped lang="scss">
.chat-page-wrapper {
	height: 100vh;
	display: flex;
	flex-direction: column;
}

.chat-top-strip {
	height: 48px;
	background: #e1e6f0; // 与 Home.vue 左侧导航保持一致
	display: flex;
	align-items: center;
}

.chat-top-inner {
	flex: 1;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 32px;
	box-sizing: border-box;
}

.chat-top-center {
	display: flex;
	align-items: center;
	gap: 8px;
	position: relative;
	left: -120px; // 略向左偏，与 Chat.vue 视觉保持一致
}

.chat-top-search {
	width: clamp(480px, calc(480px + (100vw - 1440px) * 0.6667), 960px);
	min-width: 480px;
	max-width: calc(100vw - 64px);
}

.contact-page {
	position: relative;
	z-index: 1;
	height: calc(100vh - 48px);

	.aside {
		position: relative;
		z-index: 100;
		border-right: var(--im-border);
		background: var(--im-background);
		overflow: visible; // 允许 + 菜单溢出到中间区域

		.header {
			padding: 10px;

			.header-inner {
				display: flex;
				align-items: center;
			}

			.search-text {
				flex: 1;
				min-width: 0;
			}

			.header-plus {
				position: relative;
				margin-left: 6px;
				width: 26px;
				height: 26px;
				line-height: 24px;
				text-align: center;
				border-radius: 50%;
				border: 1px solid #d0d5e5;
				cursor: pointer;
				font-size: 18px;
				color: #666;
				background: #f7f8fc;
				transition: all 0.2s;
			}

			.header-plus:hover {
				color: #1976d2;
				border-color: #1976d2;
			}

				.header-plus-menu {
					position: absolute;
					top: 34px; // 稍微下移一点
					right: 0;  // 右边缘对齐左侧列表区域分割线，和消息页面保持一致
					width: 140px;
				background: #ffffff;
				box-shadow: var(--im-box-shadow-light);
				border-radius: 6px;
				padding: 6px 0;
				z-index: 2000; // 确保浮在中间内容之上
			}

			.header-plus-menu .menu-item {
				padding: 6px 12px;
				font-size: var(--im-font-size-small);
				color: var(--im-text-color);
				cursor: pointer;
				display: flex;
				align-items: center;
				gap: 6px;
			}

			.header-plus-menu .menu-item:hover {
				background: #f5f7fa;
			}
		}

		// 左侧三行菜单区域
		.contact-menu {
			padding: 8px 10px 12px 10px;
			background-color: #f3f3f3;
			box-sizing: border-box;

			.menu-item {
				height: 44px;
				padding: 0 16px;
				margin-bottom: 0;
				border-radius: 8px;
				background-color: #ffffff;
				font-size: 14px;
				color: #333;
				cursor: pointer;
				transition: background-color 0.2s, color 0.2s;
				display: flex;
				align-items: center;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				gap: 8px;
			}

			.menu-item .menu-icon {
				position: relative;
				width: 18px;
				height: 18px;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
				font-size: 17px;
				color: inherit; /* 图标颜色跟随文字 */
			}

			.menu-icon-new .menu-icon-plus {
				position: absolute;
				left: -5px;
				top: 3px;
				font-size: 12px;
				line-height: 12px;
				font-weight: 600;
			}

			.menu-icon-friend {
				color: #222222;
			}

			.menu-icon-group .menu-icon-group-mini {
				position: absolute;
				right: -6px;
				bottom: 0;
				font-size: 10px;
				background: #ffffff;
				border-radius: 50%;
			}

			.menu-item.active .menu-icon-group .menu-icon-group-mini {
				background: #d4def5;
			}

			.menu-item:hover {
				background-color: #f5f7fa;
			}

			.menu-item.active {
				color: #1a73e8;          /* 蓝色文字 */
				font-weight: 500;
				background-color: #d4def5; /* 与聊天列表类似的浅蓝高亮 */
			}
		}

	// 右侧详情容器层级保持较低，避免盖住左侧菜单
	.container {
		position: relative;
		z-index: 0;
	}

		.new-friend-entry {
			padding: 10px 14px;
			border-bottom: var(--im-border);
			display: flex;
			align-items: center;
			cursor: pointer;
			background-color: #ffffff;
			transition: background-color 0.2s;

			&:hover {
				background-color: #f0f4ff;
			}

			.new-friend-left {
				margin-right: 10px;
			}

			.new-friend-avatar {
				width: 36px;
				height: 36px;
				border-radius: 50%;
				background-color: #1976d2;
				display: flex;
				align-items: center;
				justify-content: center;
				color: #ffffff;
				font-size: 20px;
			}

			.new-friend-texts {
				flex: 1;
				display: flex;
				flex-direction: column;
				min-width: 0;

				.title {
					font-size: var(--im-font-size);
					color: var(--im-text-color);
				}

				.subtitle {
					margin-top: 2px;
					font-size: var(--im-font-size-small);
					color: var(--im-text-color-light);
				}
			}

			.new-friend-badge {
				min-width: 24px;
				height: 20px;
				padding: 0 6px;
				border-radius: 10px;
				background-color: #ff4d4f;
				color: #ffffff;
				font-size: 12px;
				text-align: center;
				line-height: 20px;
			}
		}

		.start-group-entry {
			padding: 8px 14px 10px 14px;
			border-bottom: var(--im-border);
			display: flex;
			align-items: center;
			cursor: pointer;
			background-color: #ffffff;
			transition: background-color 0.2s;

			&:hover {
				background-color: #f0f4ff;
			}

			.start-group-left {
				margin-right: 10px;
			}

			.start-group-avatar {
				position: relative;
				width: 36px;
				height: 36px;
				border-radius: 50%;
				background-color: #1976d2;
				color: #ffffff;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 18px;
				overflow: hidden;
			}

			.start-group-avatar .user-left,
			.start-group-avatar .user-right {
				position: absolute;
			}

			.start-group-avatar .user-left {
				left: 6px;
			}

			.start-group-avatar .user-right {
				right: 6px;
			}

			.start-group-texts {
				flex: 1;
				display: flex;
				flex-direction: column;
				min-width: 0;

				.title {
					font-size: var(--im-font-size);
					color: var(--im-text-color);
				}

				.subtitle {
					margin-top: 2px;
					font-size: var(--im-font-size-small);
					color: var(--im-text-color-light);
				}
			}
		}

		.contact-items {
			padding: 0 10px 10px 10px;

			.section-title {
				margin: 8px 0;
				font-size: var(--im-font-size);
				color: var(--im-text-color-light);
				text-align: center;
			}

			.letter {
				display: none;
			}

			.divider {
				border-top: var(--im-border);
				margin: 6px 0;
			}

			.search-user-item {
				display: block;
				padding: 4px 0;
			}
		}
	}

	.container {
		flex: 1;
		position: relative;
		background-color: #ffffff;

		.detail-main {
			position: relative;
			padding: 0;
		}

		.contact-content-title {
			position: absolute;
			top: 12px;
			left: 0;
			right: 0;
			z-index: 3;
			text-align: center;
			font-size: 16px;
			font-weight: 600;
			color: #111827;
			line-height: 24px;
			pointer-events: none;
		}

		.list-aside {
			position: relative;
			height: 100%;
			box-sizing: border-box;
			overflow: hidden;

			&.has-content-title {
				.contact-items {
					padding-top: 58px;
				}
			}

			&.is-new-friends.has-content-title {
				.contact-items {
					padding-top: 44px;
				}
			}

			.friend-index-rail {
				position: absolute;
				top: 58px;
				right: 4px;
				bottom: 8px;
				z-index: 5;
				width: 22px;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 2px;
				pointer-events: none;
			}

			.contact-group-letter {
				width: 20px;
				height: 20px;
				padding: 0;
				border: 0;
				border-radius: 50%;
				background-color: transparent;
				color: #111827;
				font-size: 12px;
				line-height: 20px;
				font-weight: 500;
				text-align: center;
				cursor: pointer;
				pointer-events: auto;

				&:hover {
					background-color: #eef3ff;
				}

				&.active {
					color: #ffffff;
					background-color: #5677fc;
				}
			}
		}

		.contact-items {
			height: 100%;
			padding: 0 10px 10px 10px;
			box-sizing: border-box;

			.section-title {
				margin: 8px 0;
				font-size: var(--im-font-size);
				color: var(--im-text-color-light);
				text-align: center;
			}

			.letter {
				display: none;
			}

			.friend-request-summary {
				display: grid;
				grid-template-columns: 42px 44px minmax(64px, 1fr) 52px;
				grid-template-rows: 24px 28px;
				column-gap: 8px;
				row-gap: 4px;
				align-items: center;
				min-height: 86px;
				padding: 14px 10px 10px 2px;
				border-bottom: 1px solid #f0f0f0;
				box-sizing: border-box;
			}

			.friend-request-avatar {
				grid-column: 1;
				grid-row: 1 / 3;
			}

			.friend-request-name {
				grid-column: 2;
				grid-row: 1;
				min-width: 0;
				font-size: 15px;
				color: #111827;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.friend-request-time {
				grid-column: 3;
				grid-row: 1;
				min-width: 0;
				font-size: 13px;
				color: #9ca3af;
				text-align: center;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.friend-request-status {
				grid-column: 4;
				grid-row: 1;
				font-size: 12px;
				color: #1976d2;
				white-space: nowrap;
			}

			.friend-request-status.status-2 {
				color: #f56c6c;
			}

			.friend-request-buttons {
				grid-column: 3 / 5;
				grid-row: 2;
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 4px;

				.el-button {
					min-width: 32px;
					height: 22px;
					padding: 2px 5px;
					font-size: 11px;
					line-height: 16px;
					border-radius: 3px;
				}

				.el-button + .el-button {
					margin-left: 0;
				}
			}

			.list-empty-tip {
				padding-top: 90px;
				text-align: center;
				color: var(--im-text-color-light);
				font-size: 14px;
			}

			.contact-group {
				position: relative;
				padding-right: 28px;
			}

			.divider {
				border-top: var(--im-border);
				margin: 6px 0;
			}

			.search-user-item {
				display: flex;
				align-items: center;
				gap: 8px;
				padding: 4px 0;
			}

			.global-search-block {
				margin-bottom: 10px;
			}

			.global-search-row {
				padding: 3px 0;
				cursor: pointer;
			}

			.global-message-row {
				display: flex;
				align-items: center;
				gap: 10px;
				min-height: 54px;
				padding: 8px 4px;
				border-radius: 6px;
				cursor: pointer;
			}

			.global-message-row:hover,
			.global-search-row:hover {
				background-color: #f5f7fa;
			}

			.global-message-info {
				flex: 1;
				min-width: 0;
			}

			.global-message-title {
				font-size: 14px;
				color: #111827;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.global-message-content {
				margin-top: 4px;
				font-size: 12px;
				color: #9ca3af;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.search-user-main {
				flex: 1;
				min-width: 0;
			}

			.search-user-actions {
				flex-shrink: 0;

				.el-button {
					width: 62px;
					padding-left: 0;
					padding-right: 0;
					text-align: center;
				}

				.el-button--primary {
					background-color: #1976d2;
					border-color: #1976d2;
				}

				.el-button--primary:hover,
				.el-button--primary:focus {
					background-color: #0d7deb;
					border-color: #0d7deb;
				}
			}
		}

		.empty-tip {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			color: var(--im-text-color-light);
		}

		.user-detail-page {
			height: 100%;
			display: flex;
			flex-direction: column;
			padding: 20px 40px;
			overflow-y: auto;

			.detail-header {
				text-align: center;
				margin-bottom: 20px;
				border-bottom: 1px solid #f0f0f0;
				padding-bottom: 15px;

				.user-name {
					font-size: 20px;
					font-weight: 600;
					color: #111827;
				}
			}

			.detail-body {
				flex: 1;
				overflow-y: auto;

				.avatar-section {
					text-align: center;
					margin: 30px 0 25px 0;
				}

				.basic-section {
					text-align: center;
					margin-bottom: 20px;

					.user-display-name {
						font-size: 22px;
						font-weight: 600;
						color: #111827;
					}
				}

				.info-section {
					padding: 0 10px;

					.info-row {
						display: flex;
						padding: 14px 0;
						border-bottom: 1px solid #f5f5f5;
						text-align: left;

						.label {
							width: 140px;
							font-size: 15px;
							color: #6b7280;
							flex-shrink: 0;
						}

						.value {
							flex: 1;
							font-size: 15px;
							color: #111827;
							word-break: break-all;
							min-width: 0;
						}

						&:last-child {
							border-bottom: none;
						}
					}

					.value.photos {
						.photo-container {
							display: flex;
							flex-wrap: wrap;
							gap: 8px;
						}
						.photo-item {
							width: 80px;
							height: 80px;
							border-radius: 6px;
							overflow: hidden;
							border: 1px solid #e5e7eb;
							background: #f9fafb;
						}
						.photo-image {
							width: 100%;
							height: 100%;
							object-fit: cover;
							display: block;
							cursor: pointer;
						}
						.photo-placeholder {
							color: #9ca3af;
						}
					}
				}
			}

			.detail-footer {
				display: flex;
				justify-content: center;
				gap: 16px;
				padding: 12px 0 4px 0;
				border-top: 1px solid #f0f0f0;
				margin-bottom: 28px;

				.el-button {
					min-width: 140px;
				}

				/* 好友详情底部“发送消息”按钮使用主色蓝 */
				.el-button--primary {
					background-color: #d4def5 !important; /* Web 主色：与聊天选中气泡/顶部条同一色系的浅蓝 */
					border-color: #d4def5 !important;
					color: #111827 !important;
				}

				.el-button--primary:hover,
				.el-button--primary:focus {
					background-color: #c3cfef !important; /* 略深一点的悬停色 */
					border-color: #c3cfef !important;
					color: #111827 !important;
				}
			}
		}

		.group-detail-page {
			height: 100%;
			overflow-y: auto;
			background-color: #ffffff;
		}

		.contact-group-detail {
			width: 640px;
			max-width: 100%;
			min-height: 100%;
			box-sizing: border-box;
			border-right: 1px solid #eef0f4;
			background-color: #ffffff;
		}
	}
}
</style>
